import React, { useEffect, useState, useRef } from 'react';
import {
  VStack,
  Box,
  Text,
  Flex,
  Icon,
  useColorModeValue,
  Skeleton,
  HStack,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from '@chakra-ui/react';
import { FiStar, FiSmile, FiHeart, FiMeh, FiFrown } from 'react-icons/fi';
import { useTags } from '../../../contexts/TagsContext';
import { JournalEntry } from '../../../types/journal';
import { useJournal } from '../../../contexts/JournalContext';
import { useEncryption } from '../../../contexts/EncryptionContext';
import { DeleteIcon } from '@chakra-ui/icons';

interface JournalEntryListProps {
  entries: JournalEntry[];
  selectedEntry: JournalEntry | null;
  onSelectEntry: (entry: JournalEntry | null) => void;
  isLoading: boolean;
}

const getMoodIcon = (label?: string) => {
  if (!label) return FiMeh;
  switch (label.toLowerCase()) {
    case 'amazing': return FiStar;
    case 'happy': return FiSmile;
    case 'good': return FiHeart;
    case 'okay': return FiMeh;
    case 'down': return FiFrown;
    default: return FiMeh;
  }
};

const getMoodColor = (label?: string) => {
  if (!label) return 'gray.400';
  switch (label.toLowerCase()) {
    case 'amazing': return 'yellow.400';
    case 'happy': return 'green.400';
    case 'good': return 'blue.400';
    case 'okay': return 'purple.400';
    case 'down': return 'red.400';
    default: return 'gray.400';
  }
};

const JournalEntryList: React.FC<JournalEntryListProps> = ({
  entries,
  selectedEntry,
  onSelectEntry,
  isLoading,
}) => {
  const [decryptedContents, setDecryptedContents] = useState<Record<string, string>>({});
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const unselectedBg = useColorModeValue('white', 'gray.800');
  const dateColor = useColorModeValue('gray.600', 'gray.400');
  const emptyTextColor = useColorModeValue('gray.600', 'gray.400');
  const { tags } = useTags();
  const tagMap = new Map(tags.map(tag => [tag.id, tag]));
  const { deleteEntry } = useJournal();
  const { decrypt } = useEncryption();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  useEffect(() => {
    const decryptEntries = async () => {
      const decrypted: Record<string, string> = {};
      for (const entry of entries) {
        try {
          decrypted[entry.id] = await decrypt(entry.encrypted_content);
        } catch (error) {
          console.error('Failed to decrypt entry:', error);
          decrypted[entry.id] = 'Error decrypting entry';
        }
      }
      setDecryptedContents(decrypted);
    };

    decryptEntries();
  }, [entries, decrypt]);

  const getPreview = (entry: JournalEntry): string => {
    const decrypted = decryptedContents[entry.id] || '[Encrypted Content]';
    return decrypted.length > 100 ? decrypted.substring(0, 100) + '...' : decrypted;
  };

  const handleDelete = async (entryId: string) => {
    try {
      await deleteEntry(entryId);
      if (selectedEntry?.id === entryId) {
        onSelectEntry(null);
      }
      toast({
        title: 'Journal entry deleted',
        status: 'success',
        duration: 3000,
      });
      setIsDeleteDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error deleting entry',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const getFormattedDate = (entry: JournalEntry): string => {
    try {
      const dateToFormat = entry.entry_date || entry.created_at;
      if (!dateToFormat) return 'No date';
      
      const date = new Date(dateToFormat);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="100px" />
        ))}
      </VStack>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      {entries.map((entry) => (
        <Box
          key={entry.id}
          p={4}
          borderRadius="md"
          cursor="pointer"
          bg={selectedEntry?.id === entry.id ? selectedBg : unselectedBg}
          _hover={{
            bg: selectedBg,
          }}
          onClick={() => onSelectEntry(entry)}
        >
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="sm" color={dateColor}>
              {getFormattedDate(entry)}
            </Text>
            <IconButton
              aria-label="Delete entry"
              icon={<DeleteIcon />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              position="relative"
              zIndex={1}
              onClick={(e) => {
                e.stopPropagation();
                setEntryToDelete(entry.id);
                setIsDeleteDialogOpen(true);
              }}
            />
          </Flex>
          
          <Text noOfLines={2} fontSize="sm">
            {getPreview(entry)}
          </Text>

          <Flex justify="space-between" align="center" mt={2}>
            <Flex gap={2} flexWrap="wrap" flex={1}>
              {entry.tags?.map((tagId) => {
                const tag = tagMap.get(tagId);
                if (!tag) return null;
                return (
                  <Box
                    key={tagId}
                    px={2}
                    py={1}
                    borderRadius="full"
                    bg={tag.color + '20'}
                    fontSize="xs"
                  >
                    <Flex align="center">
                      <Box
                        w={2}
                        h={2}
                        borderRadius="full"
                        bg={tag.color}
                        mr={1}
                      />
                      {tag.decrypted_name}
                    </Flex>
                  </Box>
                );
              })}
            </Flex>

            {entry.decrypted_mood_label && (
              <HStack spacing={1} ml={2}>
                <Icon
                  as={getMoodIcon(entry.decrypted_mood_label)}
                  color={getMoodColor(entry.decrypted_mood_label)}
                />
                <Text fontSize="xs" color={getMoodColor(entry.decrypted_mood_label)}>
                  {entry.decrypted_mood_label}
                </Text>
              </HStack>
            )}
          </Flex>
        </Box>
      ))}

      {entries.length === 0 && (
        <Text
          textAlign="center"
          color={emptyTextColor}
          py={8}
        >
          No entries yet. Create your first journal entry!
        </Text>
      )}

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Journal Entry
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => entryToDelete && handleDelete(entryToDelete)}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default JournalEntryList; 